import { ChoiceLayout } from '@ee/common/enums';
import { CustomField } from './custom-field.model';
import { LayoutRule } from './layout-rule.model';

/**
 * The 12-column grid intake layout v2 is expressed in.
 *
 * Twelve columns because every legacy width maps exactly — 25→3, 33→4, 50→6, 75→9, 100→12 — so the
 * migration rounds nothing. Keep this in step with `LayoutGrid.kt`; the two are one model expressed twice.
 */
export const LAYOUT_COLUMNS = 12;
export const LAYOUT_MIN_SPAN = 2;
export const LAYOUT_MAX_SPAN = LAYOUT_COLUMNS;

/** 1 = the legacy `order` + `columns` layout. 2 = explicit rows. The renderer serves both. */
export const LAYOUT_VERSION_LEGACY = 1;
export const LAYOUT_VERSION_ROWS = 2;

/** Spans that have an exact legacy percentage, and so can roll back to v1. */
export const LEGACY_SPANS: number[] = [3, 4, 6, 9, 12];

/**
 * How wide a field usually wants to be. A hint, not a width: it seeds a new placement's span and then
 * stops applying, so changing it moves every placement that still inherits and leaves overridden ones
 * alone. Resolves to 33/50/75/100% on a 12-column page.
 *
 * Lives here rather than beside the builder so `FieldDefinition` can reach it without a cycle.
 */
export type SizeHint = 'SHORT' | 'MEDIUM' | 'LONG' | 'FULL';

export const SIZE_HINT_SPAN: Record<SizeHint, number> = {
  SHORT: 4,
  MEDIUM: 6,
  LONG: 9,
  FULL: 12
};

export const SIZE_HINT_LABEL: Record<SizeHint, string> = {
  SHORT: 'Short',
  MEDIUM: 'Medium',
  LONG: 'Long',
  FULL: 'Full'
};

/**
 * The hint a span sits closest to, for reading a definition's width back off its placements.
 *
 * Nearest rather than exact: an authored span of 5 or 7 has no hint of its own, and refusing to answer
 * would make the registry's SIZE column blank for a field that plainly has a width.
 */
export function sizeHintForSpan(span: number): SizeHint {
  const hints = Object.keys(SIZE_HINT_SPAN) as SizeHint[];
  return hints.reduce((closest, hint) =>
    Math.abs(SIZE_HINT_SPAN[hint] - span) < Math.abs(SIZE_HINT_SPAN[closest] - span) ? hint : closest
  );
}

/** The hint a legacy `columns` width implies, for a definition that has never carried one. */
export function sizeHintForColumns(columns: number | undefined | null): SizeHint {
  switch (columns) {
    case 2: return 'MEDIUM';
    case 3: return 'LONG';
    case 4: return 'FULL';
    default: return 'SHORT';
  }
}

export type LayoutItemKind = 'FIELD' | 'COMPOSITE' | 'HEADING' | 'RICH_TEXT' | 'DIVIDER';

export type CompositeType = 'ADDRESS' | 'TENANT_DETAILS' | 'SCRA_CHECK' | 'PACER_DETAILS';

export type PlacementLockReason = 'ALWAYS_REQUIRED' | 'CLIENT_CREATION' | 'PRODUCT_OWNED';

export const LOCK_REASON_TEXT: Record<PlacementLockReason, string> = {
  ALWAYS_REQUIRED: 'Always required — a case cannot be filed without it.',
  CLIENT_CREATION: 'Needed to create the client\'s account, so it stays in this section.',
  PRODUCT_OWNED: 'Part of this step, so it cannot be moved or removed.'
};

/** Client-creation fields are additionally pinned to their section, not just un-removable. */
export const LOCK_PINS_TO_SECTION: Record<PlacementLockReason, boolean> = {
  ALWAYS_REQUIRED: false,
  CLIENT_CREATION: true,
  PRODUCT_OWNED: true
};

export const BLOCK_KINDS: LayoutItemKind[] = ['HEADING', 'RICH_TEXT', 'DIVIDER'];

/**
 * A *placement*: the join record between a registry field and a surface. Span lives here, not on the
 * field — the same field may sit at 6/12 on intake and 12/12 on a workflow step.
 */
export interface LayoutItem {
  kind: LayoutItemKind;
  /** Storage key of the placed field. Absent for content blocks. */
  field_key?: string | null;
  composite_type?: CompositeType | null;
  /** 2..12. A row may sum to less than 12; trailing free space is legal. */
  span: number;
  /**
   * 1..12. Absent means "start where the previous item ends"; a value lets a field sit past a gap.
   * Ignored below 1024px, where the renderer collapses spans.
   */
  col_start?: number | null;
  /**
   * The choices this form shows for a SELECT / RADIO / MULTI_SELECT field, as stored values: a subset of
   * the field's own. Null or absent shows them all. Only the intake form narrows.
   */
  visible_options?: string[] | null;
  /** RADIO / MULTI_SELECT only: this form's stacked-or-in-a-row, over the field's own. Absent follows the field. */
  choice_layout?: ChoiceLayout | null;
  /** Text of a heading, or the sanitized HTML of a rich-text block. */
  text?: string | null;
  /** Label on this surface. Null/absent means inherited from the registry definition. */
  label_override?: string | null;
  /**
   * The line under the control, on this surface.
   *
   * On the placement rather than the definition because a *document* needs it there: one form asks for
   * three `LEASE_AGREEMENT` uploads — a lease, its addenda and a HAP contract — and each explains
   * itself differently. Null means the definition's hint, where there is one.
   */
  hint?: string | null;
  /**
   * A link offered beside the question, rendered as an info icon.
   *
   * v1 offers one on the rental-assistance declaration and eighteen forms set it, most to their state's
   * ERAP page. It is per-placement for the same reason the hint is: it explains this question here.
   */
  more_info_link?: string | null;
  required_intake?: boolean | null;
  required_workflow?: boolean | null;
  lock_reason?: PlacementLockReason | null;
  /** Derived, never authored: what v1 would have stored, kept for one release. */
  derived_columns?: number | null;
}

/**
 * Rows are explicit objects with stable ids because a drop target has to be addressable before anything
 * can be dragged into it.
 */
export interface LayoutRow {
  id: string;
  items: LayoutItem[];
}

export interface IntakeFormPage {
  id: string;
  name: string;
  /** Page 1 asks the account questions the signed-in portal already knows. */
  public_only?: boolean;
  /** The first page and Review cannot be moved or removed. */
  fixed?: boolean;
  /** The editable "What happens next" page shown after submit on the public form. */
  post_submit?: boolean;
  section_keys: string[];
}

export function isBlock(item: LayoutItem): boolean {
  return BLOCK_KINDS.includes(item.kind);
}

/** One past the last column the row occupies, honouring `col_start` — 1 for an empty row. */
export function rowExtent(row: LayoutRow): number {
  let cursor = 1;
  row.items.forEach(item => {
    const start = item.col_start ?? cursor;
    cursor = Math.max(cursor, start + item.span);
  });
  return cursor;
}

/** Columns the row occupies from column 1, gaps included. */
export function rowUsed(row: LayoutRow): number {
  return rowExtent(row) - 1;
}

export function rowFree(row: LayoutRow): number {
  return LAYOUT_COLUMNS - rowUsed(row);
}

/** Content blocks never count as fields. */
export function rowFieldCount(row: LayoutRow): number {
  return row.items.filter(item => !isBlock(item)).length;
}

export function isLegacyRepresentable(span: number): boolean {
  return LEGACY_SPANS.includes(span);
}

export function spanToPercent(span: number): number {
  return Math.round((span * 100) / LAYOUT_COLUMNS);
}

/** `4/12 · 33%` — the builder shows both units so neither has to be inferred. */
export function spanLabel(span: number): string {
  return `${span}/${LAYOUT_COLUMNS} · ${spanToPercent(span)}%`;
}

/**
 * Legacy `CustomField.columns` (quarters of the intake grid) → span.
 *
 * Only 2/3/4 get a span class in `_responsive-form-styles.scss`; everything else — including the 0 that
 * older documents carry — renders as a single quarter column. Reproduce that rather than "fixing" it,
 * or the migrated form stops matching what the firm sees today.
 */
export function spanForLegacyColumns(columns: number | undefined | null): number {
  switch (columns) {
    case 2: return 6;
    case 3: return 9;
    case 4: return 12;
    default: return 3;
  }
}

/** Span → the `columns` v1 would have stored, for the one release that still reads v1. */
export function legacyColumnsForSpan(span: number): number {
  if (span >= 12) return 4;
  if (span >= 9) return 3;
  if (span >= 6) return 2;
  return 1;
}

export function clampSpan(span: number): number {
  return Math.min(LAYOUT_MAX_SPAN, Math.max(LAYOUT_MIN_SPAN, span));
}

/** A section is representable in v1 only while every span has an exact legacy equivalent. */
export function sectionIsLegacyRepresentable(rows: LayoutRow[]): boolean {
  return rows.every(row => row.items.every(item => isLegacyRepresentable(item.span)));
}

export function nonLegacySpanCount(rows: LayoutRow[]): number {
  return rows.reduce(
    (count, row) => count + row.items.filter(item => !isLegacyRepresentable(item.span)).length,
    0
  );
}

/**
 * Turn a v1 section — an ordered list of fields each carrying a `columns` width — into explicit rows.
 *
 * The rule is the same computation the renderer already performs: convert each width to a span, then walk
 * the section in order and greedily pack into rows until the next field would exceed 12. That is what the
 * CSS grid does with `grid-column: span n`, so the result is identical by construction.
 *
 * Hidden fields occupy no grid cell in v1, so they get no placement here either — otherwise the wrap point
 * they currently collapse would move and the section would reflow. Their definitions are untouched.
 *
 * Kept in step with `IntakeLayoutMigrator.kt`; the builder's preview needs the same answer the server gives.
 */
export function migrateFieldsToRows(fields: CustomField[]): LayoutRow[] {
  const ordered = [...fields]
    .filter(field => !field.hide && !field.hide_from_intake)
    .map((field, index) => ({ field, index }))
    .sort((a, b) => (a.field.order ?? 0) - (b.field.order ?? 0) || a.index - b.index)
    .map(entry => entry.field);

  const rows: LayoutRow[] = [];
  let current: LayoutRow | null = null;

  ordered.forEach(field => {
    if (!field.field) return;
    const span = spanForLegacyColumns(field.columns);
    const item: LayoutItem = {
      kind: 'FIELD',
      field_key: field.field,
      span,
      derived_columns: legacyColumnsForSpan(span)
    };
    if (!current || rowUsed(current) + span > LAYOUT_COLUMNS) {
      current = { id: `r${rows.length + 1}`, items: [item] };
      rows.push(current);
    } else {
      current.items.push(item);
    }
  });

  return rows;
}

/** A titled band of rows — the layout shape of a section, with nothing attorney-specific on it. */
export interface LayoutSection {
  key: string;
  title?: string | null;
  description?: string | null;
  rows: LayoutRow[];
}

/** Mirrors the backend `FormLayout` (EE-412): what `CaseTemplate.intake_layout` stores. */
export interface FormLayout {
  pages: IntakeFormPage[];
  sections: LayoutSection[];
  rules: LayoutRule[];
}

export function emptyFormLayout(): FormLayout {
  return { pages: [], sections: [], rules: [] };
}
